/* eslint-disable */

import { AxiosInstance } from 'axios';
import { ApiClient } from '../apiClient';

export interface CurrencyRate {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  timestamp: number;
}

export interface CurrencyConversionRequest {
  currencyIn: string;
  currencyOut: string;
  amount: number;
}

export interface CurrencyConversionResult {
  fromCurrency: string;
  toCurrency: string;
  originalAmount: number;
  convertedAmount: number;
  rate: number;
  cached: boolean;
}

export class CurrencyService {
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private static readonly CACHE_KEY_PREFIX = 'currency_rate_';
  private client: AxiosInstance;

  constructor(apiClient: ApiClient) {
    this.client = (apiClient as any).client;
  }

  /**
   * Get currency exchange rate with caching
   */
  async getRate(fromCurrency: string, toCurrency: string): Promise<CurrencyRate> {
    const cacheKey = this.getCacheKey(fromCurrency, toCurrency);
    
    // Try to get from cache first
    const cachedRate = this.getCachedRate(cacheKey);
    if (cachedRate && this.isCacheValid(cachedRate.timestamp)) {
      return cachedRate;
    }

    try {
      // Fetch from API
      const response = await this.client.get(
        `/payments/rate/${fromCurrency}/${toCurrency}`
      );

      const rate: CurrencyRate = {
        fromCurrency,
        toCurrency,
        rate: response.data,
        timestamp: Date.now()
      };

      // Cache the result
      this.setCachedRate(cacheKey, rate);
      
      return rate;
    } catch (error) {
      // If API fails and we have expired cache, use it as fallback
      if (cachedRate) {
        console.warn('API failed, using expired cache for currency rate', { fromCurrency, toCurrency, error });
        return cachedRate;
      }
      throw error;
    }
  }

  /**
   * Convert amount from one currency to another
   */
  async convertCurrency({ currencyIn, currencyOut, amount }: CurrencyConversionRequest): Promise<CurrencyConversionResult> {
    // If same currency, return as-is
    if (currencyIn === currencyOut) {
      return {
        fromCurrency: currencyIn,
        toCurrency: currencyOut,
        originalAmount: amount,
        convertedAmount: amount,
        rate: 1,
        cached: false
      };
    }

    const cacheKey = this.getCacheKey(currencyIn, currencyOut);
    const cachedRate = this.getCachedRate(cacheKey);
    const isCached = cachedRate && this.isCacheValid(cachedRate.timestamp);

    const rate = await this.getRate(currencyIn, currencyOut);
    const convertedAmount = amount * rate.rate;

    return {
      fromCurrency: currencyIn,
      toCurrency: currencyOut,
      originalAmount: amount,
      convertedAmount: Math.round(convertedAmount * 100) / 100, // Round to 2 decimal places
      rate: rate.rate,
      cached: !!isCached
    };
  }

  /**
   * Clear all cached rates
   */
  clearCache(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CurrencyService.CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  }

  /**
   * Clear specific currency pair cache
   */
  clearCacheForPair(fromCurrency: string, toCurrency: string): void {
    const cacheKey = this.getCacheKey(fromCurrency, toCurrency);
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(cacheKey);
    }
  }

  private getCacheKey(fromCurrency: string, toCurrency: string): string {
    return `${CurrencyService.CACHE_KEY_PREFIX}${fromCurrency}_${toCurrency}`;
  }

  private getCachedRate(cacheKey: string): CurrencyRate | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      return JSON.parse(cached) as CurrencyRate;
    } catch (error) {
      console.warn('Failed to parse cached currency rate', { cacheKey, error });
      // Remove invalid cache entry
      localStorage.removeItem(cacheKey);
      return null;
    }
  }

  private setCachedRate(cacheKey: string, rate: CurrencyRate): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      localStorage.setItem(cacheKey, JSON.stringify(rate));
    } catch (error) {
      console.warn('Failed to cache currency rate', { cacheKey, error });
    }
  }

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < CurrencyService.CACHE_DURATION;
  }
}
